<script setup lang="ts">
import { useOrganizationIntegrationStore } from '@/store';
import { OrganizationIntegrationCustomerInterface } from '@/types/OrganizationInterface';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';

const organizationIntegrationStore = useOrganizationIntegrationStore();
const { error, message } = storeToRefs(organizationIntegrationStore);
const route = useRoute();
const orgId = ref<number>();
const rules = computed(() => {
  return { required };
});

const selectedCustomer = ref();
const toast = useToast();

const v$ = useVuelidate(rules, selectedCustomer);

onMounted(() => {
  init();
});

const init = async () => {
  orgId.value = parseInt(route.params.id as string);
  await organizationIntegrationStore.getIntegrationCustomers();
  await organizationIntegrationStore.getOrganizationIntegrationCustomer(orgId.value);
  if (organizationIntegrationStore.integrationCustomer)
    selectedCustomer.value = organizationIntegrationStore.integrationCustomer.id;
};

const onCancel = () => {
  resetDirty();
  selectedCustomer.value = null;
};

const onSave = async () => {
  if (selectedCustomer.value && orgId.value) {
    const payload: OrganizationIntegrationCustomerInterface = {
      organization_id: orgId.value,
      quickbooks_customer_id: selectedCustomer.value,
    };

    await organizationIntegrationStore.saveOrganizationIntegrationCustomer(payload);
    doToast();
    resetDirty();
  }
};

/**
 * reset the form dirty settting
 * which hides the footer buttons and style
 */
const resetDirty = () => {
  v$.value.$reset();
};

/**
 * Display the toast message.
 */
const doToast = () => {
  toast.add({
    severity: error.value ? 'error' : 'success',
    summary: error.value ? 'Error' : 'Saved',
    detail: message,
    life: 3000,
  });
};
</script>

<template>
  <FormCard
    title="Quickbooks"
    :dirty="v$.$anyDirty"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="v$.$invalid && v$.$dirty"
  >
    <template #form>
      <form class="w-full">
        <div class="field w-full">
          <label class="font-bold" for="customer_list">Customer</label>
          <Skeleton v-if="organizationIntegrationStore.loading" height="2.3rem" class="w-full" />
          <Dropdown
            v-else
            id="customer_list"
            v-model="selectedCustomer"
            :options="organizationIntegrationStore.customers"
            option-label="customer_name"
            option-value="id"
            placeholder="Select a Customer"
            @change="v$.$touch"
          />
        </div>
      </form>
    </template>
  </FormCard>
</template>
